import { Injectable } from "@angular/core";
import { UntypedFormGroup } from "@angular/forms";

import { combineLatest, Observable, of, Subject } from "rxjs";
import { map, mergeMap, shareReplay } from "rxjs/operators";

import { DffError } from "@dffedb/util";
import { EjendomsfaktorDefinition } from "@e-forsyning/common/ejendomsfaktor";
import { Huskeliste } from "@e-forsyning/common/huskeliste";
import { Maaler, MaalerOplysninger, MaalerType } from "@e-forsyning/common/maaler";
import { NoteBruger, NoteHenvendelsestype, NoteOmraade, NoteOpgavestyringStatus } from "@e-forsyning/common/note";

import { OpgaveFormBuilder } from "../shared/opgave.form-builder";
import { OpgaveLocalstorageService } from "../shared/opgave.localstorage.service";
import { OpgaveService } from "../shared/opgave.service";
import { KomponentType } from "./arbejdsgang/komponenter/model/komponent-type.model";
import { Opgave } from "./model/opgave.model";
import { OpgaveViewModel } from "./model/opgave.view-model";

@Injectable({ providedIn: "root" })
export class OpgaveSandbox {
    private readonly progressSubject = new Subject<number>();
    public readonly progress$ = this.progressSubject.asObservable();

    constructor(
        private readonly service: OpgaveService,
        private readonly formBuilder: OpgaveFormBuilder,
        private readonly localstorageService: OpgaveLocalstorageService
    ) {}

    public hentViewModel(opgaveId: number): Observable<OpgaveViewModel> {
        const result$ = this.hentOpgave(opgaveId).pipe(
            mergeMap((opgave) =>
                combineLatest([
                    this.service.hentKomponentTyper(),
                    this.service.hentEjendomsfaktorDefinitioner(),
                    this.service.hentMaalerePaaLager(),
                    this.service.hentMaalerTyper(),
                    this.service.hentHuskelister(),
                    this.service.hentNoteOmraader(),
                    this.service.hentNoteHenvendelsestyper(),
                    this.service.hentNoteOpgavestyringStatusser(),
                    this.service.hentNoteBrugere(),
                    this.service.hentMaalerOplysninger(opgave.installation.ejendomNr, opgave.installation.installationNr)
                ]).pipe(
                    map((value) => {
                        const komponentTyper = value[0] as KomponentType[];
                        const ejendomsfaktorDefinitioner = value[1] as EjendomsfaktorDefinition[];
                        const maalerePaaLager = value[2] as Maaler[];
                        const maalerTyper = value[3] as MaalerType[];
                        const huskelister = value[4] as Huskeliste[];
                        const noteOmraader = value[5] as NoteOmraade[];
                        const noteHenvendelsestyper = value[6] as NoteHenvendelsestype[];
                        const noteOpgavestyringStatusser = value[7] as NoteOpgavestyringStatus[];
                        const noteBrugere = value[8] as NoteBruger[];
                        const maalerOplysninger = value[9] as MaalerOplysninger;
                        const formGroup = this.formBuilder.opgaveToFormGroup(
                            opgave,
                            komponentTyper,
                            ejendomsfaktorDefinitioner,
                            maalerePaaLager,
                            maalerTyper,
                            huskelister,
                            noteOmraader,
                            noteHenvendelsestyper,
                            noteOpgavestyringStatusser,
                            noteBrugere
                        );
                        formGroup.valueChanges.subscribe(() => this.formGroupValueChanges(formGroup));
                        return {
                            komponentTyper,
                            ejendomsfaktorDefinitioner,
                            maalerOplysninger,
                            formGroup
                        };
                    })
                )
            ),
            shareReplay(1)
        );
        return result$;
    }

    public async udfoerOpgave(formGroup: UntypedFormGroup): Promise<void> {
        try {
            const opgave = this.formBuilder.formGroupToOpgave(formGroup);
            await this.service.udfoerOpgave(opgave, (progress) => this.progressSubject.next(progress));
        } catch (error) {
            throw new DffError("Der er opstået en fejl i forbindelse med udførelse af opgaven", { innerError: error });
        }
    }

    private formGroupValueChanges(formGroup: UntypedFormGroup): void {
        const opgave = this.formBuilder.formGroupToOpgave(formGroup);
        this.localstorageService.gemOpgave(opgave);
    }

    private hentOpgave(opgaveId: number): Observable<Opgave> {
        const igangvaerendeOpgave$ = this.localstorageService.hentOpgave(opgaveId);
        const result$ = igangvaerendeOpgave$.pipe(mergeMap((o) => (o ? of(o) : this.service.hentOpgave(opgaveId))));
        return result$;
    }
}
